import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default function Button({
  children,
  variant = 'primary',
  size = 'default',
  href,
  to,
  icon,
  iconPosition = 'right',
  className = '',
  loading = false,
  disabled = false,
  ...props
}) {
  const baseClasses =
    'inline-flex items-center justify-center font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary:
      'bg-brand-accent text-white hover:bg-brand-accent-hover focus:ring-brand-accent hover:shadow-glow',
    secondary:
      'border-2 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white focus:ring-brand-primary',
    ghost: 'text-brand-primary hover:text-brand-accent',
    white:
      'bg-white text-brand-primary hover:bg-white/90 focus:ring-white',
    outline:
      'border-2 border-white/30 text-white hover:bg-white/10 focus:ring-white',
  };

  const sizes = {
    small: 'px-5 py-2.5 text-sm rounded-full',
    default: 'px-8 py-4 text-base rounded-full',
    large: 'px-10 py-5 text-lg rounded-full',
  };

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  const content = (
    <>
      {loading && (
        <svg
          className="animate-spin -ml-1 mr-3 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {icon && iconPosition === 'left' && (
        <span className="mr-2">{icon}</span>
      )}
      {children}
      {icon && iconPosition === 'right' && (
        <span className="ml-2">{icon}</span>
      )}
      {!icon && variant !== 'ghost' && (
        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
      )}
    </>
  );

  // External link
  if (href) {
    return (
      <a
        href={href}
        className={`group ${classes}`}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {content}
      </a>
    );
  }

  // Internal link
  if (to) {
    const handleInternalClick = (e) => {
      // Kill all ScrollTriggers before navigation to prevent DOM conflicts
      try {
        const allTriggers = ScrollTrigger.getAll();
        allTriggers.forEach((trigger) => {
          try {
            trigger.kill(true); // Force kill immediately
          } catch (err) {
            // Ignore errors
          }
        });
      } catch (err) {
        // Ignore errors
      }
    };

    return (
      <Link to={to} className={`group ${classes}`} onClick={handleInternalClick} {...props}>
        {content}
      </Link>
    );
  }

  // Button
  return (
    <button className={`group ${classes}`} disabled={disabled || loading} {...props}>
      {content}
    </button>
  );
}
